import { useEffect, useRef, useState } from "react";
import { initialsOf } from "../lib/initials.js";
import { PATHS } from "../lib/routes.js";
import "./account-menu.css";
import { AppLink } from "./app-link.js";
import { ChevronUp, Compass, LogOut, UserRound } from "./icons.js";

/**
 * Who is signed in, and the way to their account (`account-menu` in DESIGN.md).
 *
 * The foot of the rail used to be an email address and a sign-out icon: no way
 * to change your name or password, and nowhere to put one. This row opens onto
 * both.
 *
 * A disclosure rather than an ARIA menu. Two items do not need arrow-key menu
 * semantics, and a button that says it is expanded, over ordinary links, is
 * what a screen reader already knows how to read.
 */
export function AccountMenu({
  name,
  email,
  onSignOut,
  onTakeTour,
}: {
  readonly name: string;
  readonly email: string;
  readonly onSignOut: () => void;
  readonly onTakeTour: () => void;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  // A listener on the document, only while open: a click anywhere else closes
  // it, the way every menu in every other app behaves.
  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (event.target instanceof Node && root.current?.contains(event.target) !== true) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => {
      document.removeEventListener("mousedown", close);
    };
  }, [open]);

  return (
    <div
      className="account"
      ref={root}
      onKeyDown={(event) => {
        if (event.key === "Escape") setOpen(false);
      }}
    >
      {open ? (
        <div className="account-menu" id="account-menu">
          <p className="account-menu-who">{email}</p>
          <hr className="account-menu-rule" />
          <AppLink
            href={PATHS.profile}
            className="account-menu-item"
            onNavigate={() => {
              setOpen(false);
            }}
          >
            <UserRound className="account-menu-icon" aria-hidden="true" />
            Profile
          </AppLink>
          <button
            type="button"
            className="account-menu-item"
            onClick={() => {
              setOpen(false);
              onTakeTour();
            }}
          >
            <Compass className="account-menu-icon" aria-hidden="true" />
            Take the tour
          </button>
          <hr className="account-menu-rule" />
          <button
            type="button"
            className="account-menu-item account-menu-signout"
            onClick={onSignOut}
          >
            <LogOut className="account-menu-icon" aria-hidden="true" />
            Sign out
          </button>
        </div>
      ) : null}
      <button
        type="button"
        className="account-trigger"
        aria-expanded={open}
        aria-controls="account-menu"
        onClick={() => {
          setOpen((was) => !was);
        }}
      >
        {/* The name is printed beside it, so the letters are hidden. */}
        <span className="account-avatar" aria-hidden="true">
          {initialsOf(name)}
        </span>
        <span className="account-who">
          <span className="account-name">{name}</span>
        </span>
        <ChevronUp className="account-chevron" aria-hidden="true" />
      </button>
    </div>
  );
}
